import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX, FiPlus, FiTrash2, FiSettings, FiPackage, FiTrendingUp, FiChevronDown, FiChevronUp } from 'react-icons/fi';
import Button from '../../../../../components/ui/Button';
import InputField from '../../../../../components/ui/InputField';
import Toggle from '../../../../../components/ui/Toggle';
import Autocomplete from '../../../../../components/ui/Autocomplete';
import { BranchActionConfig, ActionType, getBranchMenuItems, MenuItem } from '../../../../../utils/supabaseClient';

interface OverrideSavePayload {
    variantId?: string;
    itemId: string;
    branchActionConfigId: string;
    overrideData: any;
    actionType: string;
}

interface VariantOption {
    id: string;
    name: string;
    price?: number;
    note?: string;
    is_active?: boolean;
    is_default?: boolean;
    description?: string;
    ar?: {
        name?: string;
        description?: string;
        note?: string;
    };
}

interface ComboItem {
    id: string;
    item_name: string;
    item_id: string;
    note: string;
    quantity: number;
    useExisting?: boolean;
}

interface ComboData {
    combo_price: number;
    items: ComboItem[];
}

interface VariantConfigModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (configData: Omit<BranchActionConfig, 'id'>) => void;
    config: BranchActionConfig | null;
    actionTypes: ActionType[];
    branchId: string;
    itemId?: string;
    itemVariantId?: string;
    overrideData?: any;
    onSaveOverride?: (payload: OverrideSavePayload) => Promise<void>;
}

interface VariantOption {
    id: string;
    name: string;
    price?: number;
    note?: string;
    is_active?: boolean;
    is_default?: boolean;
    description?: string;
}

const VariantConfigModal: React.FC<VariantConfigModalProps> = ({
    isOpen,
    onClose,
    onSave,
    config,
    actionTypes,
    branchId,
    itemId,
    itemVariantId,
    overrideData,
    onSaveOverride,
}) => {
    const { t } = useTranslation();
    const [isLoading, setIsLoading] = useState(false);

    // Form state
    const [formName, setFormName] = useState('');
    const [arFormName, setArFormName] = useState('');
    const [actionType, setActionType] = useState('');
    const [isRequired, setIsRequired] = useState(false);
    const [isMultiselection, setIsMultiselection] = useState(false);
    const [minSelection, setMinSelection] = useState(0);
    const [maxSelection, setMaxSelection] = useState(1);
    const [options, setOptions] = useState<VariantOption[]>([]);
    const [ar, setAr] = useState<any>(null);
    // Combo data state (for Upgrade type)
    const [comboPrice, setComboPrice] = useState<number>(0);
    const [comboItems, setComboItems] = useState<ComboItem[]>([]);
    const [availableMenuItems, setAvailableMenuItems] = useState<MenuItem[]>([]);

    // Validation state
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [expandedOptions, setExpandedOptions] = useState<Set<string>>(new Set());

    const toggleOption = (id: string) => {
        setExpandedOptions(prev => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
    };

    // Reset form when modal opens/closes or config changes
    useEffect(() => {
        const loadMenuItems = async () => {
            if (!branchId || !isOpen) return;
            try {
                const result = await getBranchMenuItems(branchId);
                if (result.data) setAvailableMenuItems(result.data);
            } catch (err) {
                console.error('Error loading menu items:', err);
            }
        };
        loadMenuItems();

        if (isOpen) {
            if (config) {
                // Edit mode
                setFormName(config.form_name);
                setArFormName((config.ar && typeof config.ar === 'object' && 'form_name' in config.ar) ? (config.ar as any).form_name || '' : '');
                setActionType(config.action_type);
                setIsRequired(config.is_required);
                setIsMultiselection(config.is_multiselection);
                setMinSelection(config.min_selection);
                setMaxSelection(config.max_selection);

                // Handle different data structures based on action type
                if (config.action_type.toLowerCase() === 'upgrade' && config.data && typeof config.data === 'object' && !Array.isArray(config.data)) {
                    // Handle combo data structure
                    const comboData = config.data as ComboData;
                    setComboPrice(comboData.combo_price || 0);
                    setComboItems((comboData.items || []).map((item: any, index: number) => ({
                        id: `combo-item-${index}`,
                        item_name: item.item_name || '',
                        item_id: item.item_id || '',
                        note: item.note || '',
                        quantity: item.quantity || 1,
                        useExisting: !!item.item_id,
                    })));
                    setOptions([]);
                } else if (config.data && Array.isArray(config.data)) {
                    // Handle options array structure (for Selection and legacy Upgrade)
                    setOptions(config.data.map((opt: any, index: number) => ({
                        id: `option-${index}`,
                        name: opt.name || '',
                        price: opt.price || 0,
                        note: opt.note || '',
                        is_active: opt.is_active !== undefined ? opt.is_active : true,
                        is_default: opt.is_default || false,
                        description: opt.description || '',
                        ar: opt.ar || null,
                    })));
                    setComboPrice(0);
                    setComboItems([]);
                } else {
                    setOptions([]);
                    setComboPrice(0);
                    setComboItems([]);
                }
            } else {
                // Create mode
                resetForm();
            }
            setErrors({});
        }
    }, [isOpen, config]);

    // Hydrate combo item names when menu items are available
    useEffect(() => {
        if (!isOpen || !availableMenuItems.length) return;

        setComboItems(prev => prev.map(item => {
            const trimmedId = item.item_id?.trim();
            if (item.useExisting && trimmedId && !item.item_name) {
                const match = availableMenuItems.find(m => m.id === trimmedId);
                if (match) return { ...item, item_name: match.name };
            }
            return item;
        }));

    }, [availableMenuItems, isOpen]);

    // Ensure there's always a default option when form is required
    useEffect(() => {
        if (isRequired && actionType.toLowerCase() === 'selection' && options.length > 0) {
            const hasDefault = options.some(opt => opt.is_default);
            if (!hasDefault) {
                // Set the first option as default
                setOptions(prev => prev.map((opt, idx) =>
                    idx === 0 ? { ...opt, is_default: true } : opt
                ));
            }
        }
    }, [isRequired, actionType, options.length]);

    const resetForm = () => {
        setFormName('');
        setArFormName('');
        setActionType(actionTypes[0]?.name || '');
        setIsRequired(false);
        setIsMultiselection(false);
        setMinSelection(0);
        setMaxSelection(1);
        setOptions([{ id: 'option-0', name: '', price: 0, note: '', is_active: true, is_default: false, description: '' }]);
        setComboPrice(0);
        setComboItems([{ id: 'combo-item-0', item_name: '', item_id: '', note: '', quantity: 1, useExisting: true }]);
    };

    const validateForm = (): boolean => {
        const newErrors: Record<string, string> = {};

        if (!formName.trim()) {
            newErrors.formName = t('common.variants.formNameRequired', 'Form name is required');
        }

        if (!actionType) {
            newErrors.actionType = t('common.variants.actionTypeRequired', 'Action type is required');
        }

        if (minSelection < 0) {
            newErrors.minSelection = t('common.variants.minSelectionInvalid', 'Minimum selection must be 0 or greater');
        }

        if (maxSelection < minSelection) {
            newErrors.maxSelection = t('common.variants.maxSelectionInvalid', 'Maximum selection must be greater than or equal to minimum');
        }

        // Validate options/combo data based on action type
        if (actionType.toLowerCase() === 'upgrade') {
            // Check if this is a combo upgrade (has combo items) or regular upgrade (has options)
            const hasComboItems = comboItems.some(item => item.item_name.trim());
            const hasOptions = options.some(opt => opt.name.trim());
            const hasInvalidExistingSelection = comboItems.some(item => item.useExisting && !(item.item_id && item.item_id.trim()));

            if (!hasComboItems && !hasOptions) {
                newErrors.comboData = t('common.variants.comboOrOptionsRequired', 'Either combo items or upgrade options are required');
            }
            if (hasInvalidExistingSelection) {
                newErrors.comboData = t('common.variants.invalidExistingSelection', 'One or more combo items require a menu item to be selected or switch to a custom name');
            }
        } else if (actionType.toLowerCase() === 'selection') {
            if (options.length === 0 || options.every(opt => !opt.name.trim())) {
                newErrors.options = t('common.variants.optionsRequired', 'At least one option is required');
            }
            // If required, ensure at least one option is marked as default
            if (isRequired) {
                const validOptions = options.filter(opt => opt.name.trim());
                const hasDefault = validOptions.some(opt => opt.is_default);
                if (!hasDefault && validOptions.length > 0) {
                    newErrors.options = t('common.variants.requiredNeedsDefault', 'Required forms must have at least one default option');
                }
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleAddOption = () => {
        const newId = `option-${Date.now()}`;
        const newOption = { id: newId, name: '', price: 0, note: '', is_active: true, is_default: false, description: '' };
        setOptions(prev => {
            const updatedOptions = [newOption, ...prev];
            // If form is required and no default exists, set the new option as default
            if (isRequired && actionType.toLowerCase() === 'selection') {
                const hasDefault = updatedOptions.some(opt => opt.is_default);
                if (!hasDefault) {
                    updatedOptions[0] = { ...updatedOptions[0], is_default: true };
                }
            }
            return updatedOptions;
        });
        setExpandedOptions(prev => new Set(prev).add(newId));
    };

    const handleRemoveOption = (id: string) => {
        if (options.length > 1) {
            setOptions(prev => {
                const updatedOptions = prev.filter(opt => opt.id !== id);
                // If form is required and the removed option was the default, set the first remaining option as default
                if (isRequired && actionType.toLowerCase() === 'selection') {
                    const removedOption = prev.find(opt => opt.id === id);
                    if (removedOption?.is_default && updatedOptions.length > 0) {
                        updatedOptions[0] = { ...updatedOptions[0], is_default: true };
                    }
                }
                return updatedOptions;
            });
        }
    };

    const handleOptionChange = (id: string, field: keyof VariantOption, value: any) => {
        setOptions(prev => prev.map(opt =>
            opt.id === id ? { ...opt, [field]: value } : opt
        ));
    };

    // Combo item handlers
    const handleAddComboItem = () => {
        const newId = `combo-item-${Date.now()}`;
        setComboItems(prev => [{ id: newId, item_name: '', item_id: '', note: '', quantity: 1, useExisting: true }, ...prev]);
    };

    const handleRemoveComboItem = (id: string) => {
        if (comboItems.length > 1) {
            setComboItems(prev => prev.filter(item => item.id !== id));
        }
    };

    const handleComboItemChange = (id: string, field: keyof ComboItem, value: any) => {
        setComboItems(prev => prev.map(item =>
            item.id === id ? { ...item, [field]: value } : item
        ));
    };

    const handleMenuItemSelection = (comboItemId: string, selectedMenuItemId: string) => {
        const trimmedId = (selectedMenuItemId || '').trim();
        if (!trimmedId) {
            // Clear selection
            setComboItems(prev => prev.map(item =>
                item.id === comboItemId
                    ? { ...item, item_id: '', item_name: '', useExisting: false }
                    : item
            ));
            return;
        }
        const selectedMenuItem = availableMenuItems.find(item => item.id === trimmedId);
        if (selectedMenuItem) {
            setComboItems(prev => prev.map(item =>
                item.id === comboItemId
                    ? { ...item, item_id: selectedMenuItem.id, item_name: selectedMenuItem.name, useExisting: true }
                    : item
            ));
        } else {
            // No matching menu item found - treat as custom name
            setComboItems(prev => prev.map(item =>
                item.id === comboItemId
                    ? { ...item, item_id: '', item_name: selectedMenuItemId, useExisting: false }
                    : item
            ));
        }
    };

    const handleSave = async () => {
        if (!validateForm()) return;

        setIsLoading(true);
        try {
            // Determine the data structure based on action type
            let configData: Omit<BranchActionConfig, 'id'>;

            if (actionType.toLowerCase() === 'upgrade' && comboItems.some(item => item.item_name.trim())) {
                // Use combo data structure for Upgrade type
                configData = {
                    branch_id: branchId,
                    form_name: formName.trim(),
                    action_type: actionType,
                    is_required: isRequired,
                    is_multiselection: isMultiselection,
                    min_selection: minSelection,
                    max_selection: maxSelection,
                    ar: arFormName.trim() ? { form_name: arFormName.trim() } : null,
                    data: {
                        combo_price: comboPrice,
                        items: comboItems.filter(item => item.item_name.trim()).map(item => {
                            const payload: any = {
                                item_name: item.item_name.trim(),
                                quantity: item.quantity,
                            };
                            if (item.item_id && item.item_id.trim()) payload.item_id = item.item_id.trim();
                            if (item.note && item.note.trim()) payload.note = item.note.trim();
                            return payload;
                        }),
                    } as ComboData,
                };
            } else {
                // Use options array structure for Selection or legacy Upgrade
                configData = {
                    branch_id: branchId,
                    form_name: formName.trim(),
                    action_type: actionType,
                    is_required: isRequired,
                    is_multiselection: isMultiselection,
                    min_selection: minSelection,
                    max_selection: maxSelection,
                    ar: arFormName.trim() ? { form_name: arFormName.trim() } : null,
                    data: actionType.toLowerCase() === 'selection' || actionType.toLowerCase() === 'upgrade'
                        ? options.filter(opt => opt.name.trim()).map(opt => {
                            const payload: any = {
                                name: opt.name.trim(),
                                price: opt.price || 0,
                                note: opt.note?.trim() || '',
                                is_active: opt.is_active !== undefined ? opt.is_active : true,
                                is_default: opt.is_default || false,
                                description: opt.description?.trim() || '',
                            };

                            // Add Arabic translations if provided
                            const arName = opt.ar?.name?.trim() || '';
                            const arNote = opt.ar?.note?.trim() || '';
                            const arDescription = opt.ar?.description?.trim() || '';

                            if (arName || arNote || arDescription) {
                                payload.ar = {};
                                if (arName) payload.ar.name = arName;
                                if (arNote) payload.ar.note = arNote;
                                if (arDescription) payload.ar.description = arDescription;
                            }

                            return payload;
                        })
                        : null,
                };
            }

            await onSave(configData);
        } catch (error) {
            console.error('Error saving variant config:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const getActionTypeIcon = (type: string) => {
        switch (type.toLowerCase()) {
            case 'selection':
                return <FiSettings className="w-5 h-5" />;
            case 'upgrade':
                return <FiTrendingUp className="w-5 h-5" />;
            default:
                return <FiPackage className="w-5 h-5" />;
        }
    };

    const getActionTypeColor = (type: string) => {
        switch (type.toLowerCase()) {
            case 'selection':
                return 'blue';
            case 'upgrade':
                return 'purple';
            default:
                return 'orange';
        }
    };

    // Animation variants
    const overlayVariants = {
        hidden: { opacity: 0 },
        visible: { opacity: 1 }
    };

    const modalVariants = {
        hidden: { opacity: 0, scale: 0.95, y: 20 },
        visible: {
            opacity: 1,
            scale: 1,
            y: 0,
            transition: {
                type: "spring" as const,
                stiffness: 300,
                damping: 30
            }
        },
        exit: { opacity: 0, scale: 0.95, y: 20 }
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20">
                        {/* Background overlay */}
                        <motion.div
                            className="fixed inset-0 bg-black/50 backdrop-blur-sm"
                            variants={overlayVariants}
                            initial="hidden"
                            animate="visible"
                            exit="hidden"
                            onClick={onClose}
                        />

                        {/* Modal panel */}
                        <motion.div
                            className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-white dark:bg-gray-800 rounded-xl shadow-2xl"
                            variants={modalVariants}
                            initial="hidden"
                            animate="visible"
                            exit="exit"
                        >
                            {/* Header */}
                            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
                                <div>
                                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                                        {config ? t('common.variants.management.editVariant') : t('common.variants.management.addVariant')}
                                    </h2>
                                    <p className="text-gray-600 dark:text-gray-400 mt-1">
                                        {config ? t('common.variants.editDescription', 'Modify the variant configuration') : t('common.variants.createDescription', 'Configure the new variant settings')}
                                    </p>
                                </div>
                                <button
                                    onClick={onClose}
                                    className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                                >
                                    <FiX className="w-5 h-5" />
                                </button>
                            </div>

                            {/* Form Content */}
                            <div className="p-6 space-y-6">
                                {/* Basic Information - Form Name */}
                                <div className="border border-gray-200 dark:border-gray-600 rounded-xl overflow-hidden">
                                    <div className="px-4 py-3 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-700/50 border-b border-gray-200 dark:border-gray-600">
                                        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                                            {t('common.variants.formNameSection', 'Form Name')}
                                        </h3>
                                    </div>
                                    <div className="p-4 space-y-4">
                                        {/* English Form Name */}
                                        <div>
                                            <div className="flex items-center gap-2 mb-2">
                                                <span className="text-xs font-semibold text-blue-600 dark:text-blue-400 uppercase tracking-wider">
                                                    {t('common.english', 'English')}
                                                </span>
                                                <div className="flex-1 h-px bg-blue-200 dark:bg-blue-800"></div>
                                            </div>
                                            <InputField
                                                label={t('common.variants.formName')}
                                                value={formName}
                                                onChange={(e) => setFormName(e.target.value)}
                                                placeholder={t('common.variants.formNamePlaceholder')}
                                                error={errors.formName}
                                                required
                                            />
                                        </div>

                                        {/* Arabic Form Name */}
                                        <div>
                                            <div className="flex items-center gap-2 mb-2">
                                                <span className="text-xs font-semibold text-green-600 dark:text-green-400 uppercase tracking-wider">
                                                    {t('common.arabic', 'العربية')}
                                                </span>
                                                <div className="flex-1 h-px bg-green-200 dark:bg-green-800"></div>
                                            </div>
                                            <InputField
                                                label={t('common.variants.formNameAr', 'Arabic Form Name')}
                                                value={arFormName}
                                                onChange={(e) => setArFormName(e.target.value)}
                                                placeholder={t('common.variants.formNameArPlaceholder', 'أدخل اسم النموذج بالعربية')}
                                                dir="rtl"
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Action Type Selection */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                            {t('common.variants.actionType')} *
                                        </label>
                                        <select
                                            value={actionType}
                                            onChange={(e) => setActionType(e.target.value)}
                                            className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        >
                                            {actionTypes.map((type) => (
                                                <option key={type.name} value={type.name}>
                                                    {type.name}
                                                </option>
                                            ))}
                                        </select>
                                        {errors.actionType && (
                                            <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.actionType}</p>
                                        )}
                                    </div>
                                </div>

                                {/* Settings */}
                                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 space-y-4">
                                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                        {t('common.variants.settings')}
                                    </h3>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                                    {t('common.variants.required')}
                                                </label>
                                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                                    {t('common.variants.requiredDescription')}
                                                </p>
                                            </div>
                                            <Toggle
                                                enabled={isRequired}
                                                onChange={(enabled) => {
                                                    setIsRequired(enabled);
                                                    // If setting to required and no default option exists, set first option as default
                                                    if (enabled && actionType.toLowerCase() === 'selection' && options.length > 0) {
                                                        const hasDefault = options.some(opt => opt.is_default);
                                                        if (!hasDefault) {
                                                            setOptions(prev => prev.map((opt, idx) =>
                                                                idx === 0 ? { ...opt, is_default: true } : { ...opt, is_default: false }
                                                            ));
                                                        }
                                                    }
                                                }}
                                            />
                                        </div>

                                        <div className="flex items-center justify-between">
                                            <div>
                                                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                                    {t('common.variants.multiselection')}
                                                </label>
                                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                                    {t('common.variants.multiselectionDescription')}
                                                </p>
                                            </div>
                                            <Toggle
                                                enabled={isMultiselection}
                                                onChange={setIsMultiselection}
                                            />
                                        </div>
                                    </div>

                                    {isMultiselection && (
                                        <motion.div
                                            initial={{ opacity: 0, height: 0 }}
                                            animate={{ opacity: 1, height: 'auto' }}
                                            exit={{ opacity: 0, height: 0 }}
                                            className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 border-t border-gray-200 dark:border-gray-600"
                                        >
                                            <InputField
                                                label={t('common.variants.minSelection', 'Minimum Selection')}
                                                type="number"
                                                min="0"
                                                value={minSelection}
                                                onChange={(e) => setMinSelection(parseInt(e.target.value) || 0)}
                                                error={errors.minSelection}
                                            />

                                            <InputField
                                                label={t('common.variants.maxSelection', 'Maximum Selection')}
                                                type="number"
                                                min="1"
                                                value={maxSelection}
                                                onChange={(e) => setMaxSelection(parseInt(e.target.value) || 1)}
                                                error={errors.maxSelection}
                                            />
                                        </motion.div>
                                    )}
                                </div>

                                {/* Data Configuration Section */}
                                {actionType.toLowerCase() === 'upgrade' ? (
                                    <motion.div
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        className="space-y-4"
                                    >


                                        {/* Combo Price */}
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <InputField
                                                label={t('common.variants.comboPrice', 'Combo Price')}
                                                type="number"
                                                step="0.01"
                                                value={comboPrice || ''}
                                                onChange={(e) => setComboPrice(parseFloat(e.target.value) || 0)}
                                                placeholder={t('common.variants.priceDefaultPlaceholder', '0.00')}
                                            />
                                        </div>

                                        {/* Combo Items */}
                                        <div className="space-y-4">
                                            <div className="flex items-center justify-between">
                                                <h4 className="text-md font-medium text-gray-900 dark:text-white">
                                                    {t('common.variants.comboItems', 'Combo Items')}
                                                </h4>
                                                <Button
                                                    onClick={handleAddComboItem}
                                                    type="button"
                                                    variant="outline"
                                                    size="sm"
                                                    leftIcon={<FiPlus className="w-4 h-4" />}
                                                >
                                                    {t('common.variants.addComboItem', 'Add Item')}
                                                </Button>
                                            </div>

                                            {errors.comboData && (
                                                <p className="text-sm text-red-600 dark:text-red-400">{errors.comboData}</p>
                                            )}

                                            <div className="space-y-3">
                                                <AnimatePresence>
                                                    {comboItems.map((item, index) => (
                                                        <motion.div
                                                            key={item.id}
                                                            initial={{ opacity: 0, x: -20 }}
                                                            animate={{ opacity: 1, x: 0 }}
                                                            exit={{ opacity: 0, x: 20 }}
                                                            className="flex items-center gap-3 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg"
                                                        >
                                                            <div className="flex-1 space-y-3">
                                                                <div className="grid grid-cols-1 md:grid-cols-1 gap-3">
                                                                    <div>
                                                                        <div className="flex items-center justify-between mb-2">
                                                                            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                                                                {t('common.variants.itemName', 'Item name')}
                                                                            </label>
                                                                            <Toggle
                                                                                enabled={!!item.useExisting}
                                                                                onChange={(enabled: boolean) => handleComboItemChange(item.id, 'useExisting' as any, enabled as any)}
                                                                            />
                                                                        </div>
                                                                        {item.useExisting ? (
                                                                            <Autocomplete
                                                                                value={item.item_id || ''}
                                                                                label={t('common.variants.selectMenuItem', 'Select Menu Item')}
                                                                                onChange={(val: string) => handleMenuItemSelection(item.id, val)}
                                                                                options={availableMenuItems.map(menuItem => ({ value: menuItem.id, label: menuItem.name, description: menuItem.description || undefined }))}
                                                                                placeholder={t('common.variants.searchMenuItems', 'Search menu items...')}
                                                                                allowCustom={true}
                                                                                disabled={isLoading}
                                                                            />
                                                                        ) : (
                                                                            <InputField
                                                                                placeholder={t('common.variants.itemName', 'Item name')}
                                                                                value={item.item_name}
                                                                                onChange={(e) => handleComboItemChange(item.id, 'item_name', e.target.value)}
                                                                            />
                                                                        )}
                                                                    </div>
                                                                    {/* <div>
                                                                        <InputField
                                                                            placeholder={t('variants.itemId', 'Item ID (optional)')}
                                                                            value={item.item_id}
                                                                            onChange={(e) => handleComboItemChange(item.id, 'item_id', e.target.value)}
                                                                        />
                                                                        {item.useExisting && item.item_id && (
                                                                            <div className="text-sm text-gray-600 dark:text-gray-400 bg-blue-50 dark:bg-blue-900/20 p-2 rounded mt-2">
                                                                                <div><strong>{t('variants.selectedItem', 'Selected Item')}:</strong> {item.item_name}</div>
                                                                                <div><strong>{t('variants.itemId', 'ID')}:</strong> {item.item_id}</div>
                                                                            </div>
                                                                        )}
                                                                    </div> */}
                                                                </div>
                                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                                    <InputField
                                                                        type="number"
                                                                        min="1"
                                                                        placeholder={t('common.variants.quantity', 'Quantity')}
                                                                        value={item.quantity || 1}
                                                                        onChange={(e) => handleComboItemChange(item.id, 'quantity', parseInt(e.target.value) || 1)}
                                                                    />
                                                                    <InputField
                                                                        placeholder={t('common.variants.note', 'Note (optional)')}
                                                                        value={item.note}
                                                                        onChange={(e) => handleComboItemChange(item.id, 'note', e.target.value)}
                                                                    />
                                                                </div>
                                                            </div>

                                                            <button
                                                                onClick={() => handleRemoveComboItem(item.id)}
                                                                disabled={comboItems.length === 1}
                                                                className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                                            >
                                                                <FiTrash2 className="w-4 h-4" />
                                                            </button>
                                                        </motion.div>
                                                    ))}
                                                </AnimatePresence>
                                            </div>
                                        </div>
                                    </motion.div>
                                ) : actionType.toLowerCase() === 'selection' ? (
                                    <motion.div
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        className="space-y-4"
                                    >
                                        <div className="flex items-center justify-between">
                                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                                {t('common.variants.management.options', 'Options')}
                                            </h3>
                                            <Button
                                                onClick={handleAddOption}
                                                type="button"
                                                variant="outline"
                                                size="sm"
                                                leftIcon={<FiPlus className="w-4 h-4" />}
                                            >
                                                {t('common.variants.addOption', 'Add Option')}
                                            </Button>
                                        </div>

                                        {errors.options && (
                                            <p className="text-sm text-red-600 dark:text-red-400">{errors.options}</p>
                                        )}

                                        <div className="space-y-4">
                                            <AnimatePresence>
                                                {options.map((option, index) => (
                                                    <motion.div
                                                        key={option.id}
                                                        initial={{ opacity: 0, x: -20 }}
                                                        animate={{ opacity: 1, x: 0 }}
                                                        exit={{ opacity: 0, x: 20 }}
                                                        className="relative border border-gray-200 dark:border-gray-600 rounded-xl overflow-hidden"
                                                    >
                                                        {/* Option Header */}
                                                        <div 
                                                            className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-700/50 border-b border-gray-200 dark:border-gray-600 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600/50 transition-colors"
                                                            onClick={() => toggleOption(option.id)}
                                                        >
                                                            <div className="flex items-center gap-3">
                                                                <button
                                                                    type="button"
                                                                    className="p-1 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors"
                                                                >
                                                                    {expandedOptions.has(option.id) ? (
                                                                        <FiChevronUp className="w-5 h-5" />
                                                                    ) : (
                                                                        <FiChevronDown className="w-5 h-5" />
                                                                    )}
                                                                </button>
                                                                <span className="flex items-center justify-center w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-bold">
                                                                    {index + 1}
                                                                </span>
                                                                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                                                    {option.name || t('common.variants.newOption', 'New Option')}
                                                                </span>
                                                            </div>
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleRemoveOption(option.id);
                                                                }}
                                                                disabled={options.length === 1}
                                                                className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                                            >
                                                                <FiTrash2 className="w-4 h-4" />
                                                            </button>
                                                        </div>

                                                        {/* Option Content - Collapsible */}
                                                        <AnimatePresence>
                                                            {expandedOptions.has(option.id) && (
                                                                <motion.div
                                                                    initial={{ height: 0, opacity: 0 }}
                                                                    animate={{ height: 'auto', opacity: 1 }}
                                                                    exit={{ height: 0, opacity: 0 }}
                                                                    transition={{ duration: 0.2 }}
                                                                    className="overflow-hidden"
                                                                >
                                                                    <div className="p-4 space-y-4">
                                                                        {/* English Fields Section */}
                                                                        <div className="space-y-3">
                                                                            <div className="flex items-center gap-2 mb-2">
                                                                                <span className="text-xs font-semibold text-blue-600 dark:text-blue-400 uppercase tracking-wider">
                                                                                    {t('common.english', 'English')}
                                                                                </span>
                                                                                <div className="flex-1 h-px bg-blue-200 dark:bg-blue-800"></div>
                                                                            </div>
                                                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                                                <InputField
                                                                                    label={t('common.variants.optionName', 'Option Name')}
                                                                                    placeholder={t('common.variants.optionNamePlaceholder', 'Enter option name')}
                                                                                    value={option.name}
                                                                                    onChange={(e) => handleOptionChange(option.id, 'name', e.target.value)}
                                                                                />
                                                                                <InputField
                                                                                    label={t('common.variants.management.optionalPrice', 'Price')}
                                                                                    type="number"
                                                                                    step="0.01"
                                                                                    placeholder="0.00"
                                                                                    value={option.price || ''}
                                                                                    onChange={(e) => handleOptionChange(option.id, 'price', parseFloat(e.target.value) || 0)}
                                                                                />
                                                                            </div>
                                                                            <InputField
                                                                                label={t('common.variants.note', 'Note')}
                                                                                placeholder={t('common.variants.notePlaceholder', 'Add a note (optional)')}
                                                                                value={option.note || ''}
                                                                                onChange={(e) => handleOptionChange(option.id, 'note', e.target.value)}
                                                                            />
                                                                            <InputField
                                                                                label={t('common.variants.description', 'Description')}
                                                                                placeholder={t('common.variants.descriptionPlaceholder', 'Add a description (optional)')}
                                                                                value={option.description || ''}
                                                                                onChange={(e) => handleOptionChange(option.id, 'description', e.target.value)}
                                                                            />
                                                                        </div>

                                                                        {/* Arabic Fields Section */}
                                                                        <div className="space-y-3">
                                                                            <div className="flex items-center gap-2 mb-2">
                                                                                <span className="text-xs font-semibold text-green-600 dark:text-green-400 uppercase tracking-wider">
                                                                                    {t('common.arabic', 'العربية')}
                                                                                </span>
                                                                                <div className="flex-1 h-px bg-green-200 dark:bg-green-800"></div>
                                                                            </div>
                                                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                                                <InputField
                                                                                    label={t('common.variants.optionNameAr', 'Arabic Name')}
                                                                                    placeholder={t('common.variants.optionNameArPlaceholder', 'أدخل اسم الخيار')}
                                                                                    value={option.ar?.name || ''}
                                                                                    onChange={(e) => handleOptionChange(option.id, 'ar', { ...option.ar, name: e.target.value })}
                                                                                    dir="rtl"
                                                                                />
                                                                                <div></div>
                                                                            </div>
                                                                            <InputField
                                                                                label={t('common.variants.noteAr', 'Arabic Note')}
                                                                                placeholder={t('common.variants.noteArPlaceholder', 'أضف ملاحظة (اختياري)')}
                                                                                value={option.ar?.note || ''}
                                                                                onChange={(e) => handleOptionChange(option.id, 'ar', { ...option.ar, note: e.target.value })}
                                                                                dir="rtl"
                                                                            />
                                                                            <InputField
                                                                                label={t('common.variants.descriptionAr', 'Arabic Description')}
                                                                                placeholder={t('common.variants.descriptionArPlaceholder', 'أضف وصف (اختياري)')}
                                                                                value={option.ar?.description || ''}
                                                                                onChange={(e) => handleOptionChange(option.id, 'ar', { ...option.ar, description: e.target.value })}
                                                                                dir="rtl"
                                                                            />
                                                                        </div>

                                                                        {/* Toggles Row */}
                                                                        <div className="flex items-center gap-6 pt-3 border-t border-gray-100 dark:border-gray-700">
                                                                            <div className="flex items-center gap-2">
                                                                                <Toggle
                                                                                    enabled={option.is_active !== false}
                                                                                    onChange={(enabled) => handleOptionChange(option.id, 'is_active', enabled)}
                                                                                />
                                                                                <span className="text-sm text-gray-600 dark:text-gray-400">
                                                                                    {t('common.variants.active', 'Active')}
                                                                                </span>
                                                                            </div>

                                                                            <div className="flex items-center gap-2">
                                                                                <Toggle
                                                                                    enabled={option.is_default === true}
                                                                                    onChange={(enabled) => {
                                                                                        if (enabled) {
                                                                                            const updatedOptions = options.map(opt =>
                                                                                                opt.id === option.id
                                                                                                    ? { ...opt, is_default: true }
                                                                                                    : { ...opt, is_default: false }
                                                                                            );
                                                                                            setOptions(updatedOptions);
                                                                                        } else {
                                                                                            handleOptionChange(option.id, 'is_default', false);
                                                                                        }
                                                                                    }}
                                                                                />
                                                                                <span className="text-sm text-gray-600 dark:text-gray-400">
                                                                                    {t('common.variants.default', 'Default')}
                                                                                </span>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </motion.div>
                                                            )}
                                                        </AnimatePresence>
                                                    </motion.div>
                                                ))}
                                            </AnimatePresence>
                                        </div>
                                    </motion.div>
                                ) : null}

                            </div>

                            {/* Footer */}
                            <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700">
                                <Button
                                    variant="outline"
                                    onClick={onClose}
                                    disabled={isLoading}
                                >
                                    {t('common.cancel')}
                                </Button>
                                <Button
                                    onClick={handleSave}
                                    isLoading={isLoading}
                                    className="hover:from-blue-700 hover:to-purple-700"
                                >
                                    {isLoading ? t('common.saving', 'Saving...') : t('common.save')}
                                </Button>
                            </div>
                        </motion.div>
                    </div>
                </div>
            )}
        </AnimatePresence>
    );
};

export default VariantConfigModal;